import {Injectable} from "@angular/core";
import {DxConfigService} from "./dx-config.service";
import {LoggerConfig, NGXLogger, NgxLoggerLevel} from "ngx-logger";


/**
 * Сервис-обертка, предоставляющий функциональность логгирования:
 * 1. На клиенте
 * 2. Логгирование клиента с отправкой данных лога на сервер.
 *
 * `NGXLogger` - довольно интересная по функционалу либа логгирования.
 * Также она подеживает отправку логов клиента на сервер, что и послужило причиной выбора.
 * Плюс есть возможность динамического изменения уровня логгирования (и конфига в целом)
 * @publicApi
 */
@Injectable()
export class DxLogger {
  /**
   * @constructor
   * @param {NGXLogger} _ngxLogger - ngx-логер.
   * @param {DxConfigService} _configService - сервис, предоставляющий доступ к конфигурации ядра.
   */
  constructor(private _ngxLogger: NGXLogger,
              private _configService: DxConfigService) {
    let config = new LoggerConfig();
    config.level = DxLogger._mapLoggerLevel(this._configService.loggerLevel);
    this._ngxLogger.updateConfig(config);
  }

  /**
   * Логгирует с уровнем логгирования `TRACE`
   * @param {string} message - сообщение.
   * @public
   */
  public trace(message: string): void {
    this._ngxLogger.trace(message);
  }

  /**
   * Логгирует с уровнем логгирования `DEBUG`
   * @param {string} message - сообщение.
   * @public
   */
  public debug(message: string): void {
    this._ngxLogger.debug(message);
  }

  /**
   * Логгирует с уровнем логгирования `INFO`
   * @param {string} message - сообщение.
   * @public
   */
  public info(message: string): void {
    this._ngxLogger.info(message);
  }

  /**
   * Логгирует с уровнем логгирования `LOG`
   * @param {string} message - сообщение.
   * @public
   */
  public log(message: string): void {
    this._ngxLogger.log(message);
  }

  /**
   * Логгирует с уровнем логгирования `WARN`
   * @param {string} message - сообщение.
   * @public
   */
  public warn(message: string): void {
    this._ngxLogger.warn(message);
  }

  /**
   * Логгирует с уровнем логгирования `ERROR`
   * @param {string} message - сообщение.
   * @public
   */
  public error(message: string): void {
    this._ngxLogger.error(message);
  }

  /**
   * Логгирует с уровнем логгирования `FATAL`
   * @param {string} message - сообщение.
   * @public
   */
  public fatal(message: string): void {
    this._ngxLogger.fatal(message);
  }

  /**
   * Маппит обозначения уровня логгирования в dx-core на енум уровней логгирования `NgxLoggerLevel`
   * @param {string} level - уровень логгирования в строковом формате.
   * @return {NgxLoggerLevel} - уровень логгирования из `NgxLoggerLevel`.
   */
  private static _mapLoggerLevel(level: string): NgxLoggerLevel {
    switch (level) {
      case "DEBUG":
        return NgxLoggerLevel.DEBUG;
      case "ERROR":
        return NgxLoggerLevel.ERROR;
      case "FATAL":
        return NgxLoggerLevel.FATAL;
      case "INFO":
        return NgxLoggerLevel.INFO;
      case "LOG":
        return NgxLoggerLevel.LOG;
      case "OFF":
        return NgxLoggerLevel.OFF;
      case "TRACE":
        return NgxLoggerLevel.TRACE;
      case "WARN":
        return NgxLoggerLevel.WARN;
      default:
        return NgxLoggerLevel.DEBUG;
    }
  }
}
